import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { domain } from './Domain.jsx'
import useApiRequest from './useApiRequest.jsx'
import { useLoadingContext } from './LoadingContext.jsx'
import notify from './Nots.jsx'
import { 
    Mail, Phone, MapPin, Clock, MessageSquare, Send, User, 
    Building, Globe, CheckCircle, AlertCircle, Loader2,
    Facebook, Twitter, Instagram, Linkedin, Youtube
} from 'lucide-react'

const Contact = () => {
    const { getRequest, postRequest, putRequest } = useApiRequest()
    const { setLoading } = useLoadingContext()
    
    const initial = {
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    }
    
    const [data, setData] = useState(initial)
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formStatus, setFormStatus] = useState(null) // 'success', 'error', null

    const { name, email, phone, subject, message } = data

    // Enhanced validation function
    const validateForm = () => {
        let valid = true
        const newErrors = {}

        // Validate name
        if (!name.trim()) {
            newErrors.name = 'Name is required'
            valid = false
        } else if (name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters'
            valid = false
        }

        // Validate email
        if (!email.trim()) {
            newErrors.email = 'Email is required'
            valid = false
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Please enter a valid email address'
            valid = false
        }

        // Validate phone (optional but if provided, validate format)
        if (phone.trim() && !/^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/\s/g, ''))) {
            newErrors.phone = 'Please enter a valid phone number'
            valid = false
        }

        // Validate subject
        if (!subject.trim()) {
            newErrors.subject = 'Subject is required'
            valid = false
        } else if (subject.trim().length < 5) {
            newErrors.subject = 'Subject must be at least 5 characters'
            valid = false
        }

        // Validate message
        if (!message.trim()) {
            newErrors.message = 'Message is required'
            valid = false
        } else if (message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters'
            valid = false
        }

        setErrors(newErrors)
        return valid
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setData(prev => ({ ...prev, [name]: value }))
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        
        const isValid = validateForm()

        if (isValid) {
            setIsSubmitting(true)
            setFormStatus(null)
            
            try {
                console.log('Sending contact form data:', data)
                const response = await postRequest(`${domain}/contact`, data, false)
                console.log('Contact form response:', response)

                if (response.status === 201) {
                    setFormStatus('success')
                    notify("Message sent successfully! We'll get back to you soon.", "success")
                    setData(initial) // Reset form data
                } else {
                    setFormStatus('error')
                    notify("Failed to send message. Please try again.", "error")
                }
            } catch (error) {
                console.error('Contact form error:', error)
                setFormStatus('error')
                notify("An error occurred while sending your message. Please try again.", "error")
            } finally {
                setIsSubmitting(false)
            }
        }
    }

    const contactInfo = [
        {
            icon: <MapPin className="w-6 h-6" />,
            title: "Our Location",
            details: [
                "Makerere University",
                "College of Veterinary Services",
                "P.O Box 226232(U)",
                "Kampala, Uganda"
            ],
            color: "from-orange-400 to-orange-500",
            bgColor: "from-orange-50 to-orange-100"
        },
        {
            icon: <Phone className="w-6 h-6" />,
            title: "Phone Numbers",
            details: [
                "+256 700 000 000",
                "+256 800 000 000",
                "Mon-Fri: 8AM-6PM"
            ],
            color: "from-teal-400 to-teal-500",
            bgColor: "from-teal-50 to-teal-100"
        },
        {
            icon: <Mail className="w-6 h-6" />,
            title: "Email Address",
            details: [
                "info@eventplots.com",
                "support@eventplots.com",
                "sales@eventplots.com"
            ],
            color: "from-orange-500 to-orange-600",
            bgColor: "from-orange-50 to-orange-100"
        }
    ]

    const socialLinks = [
        { icon: <Facebook className="w-5 h-5" />, href: "#", label: "Facebook", color: "hover:bg-orange-500 hover:!text-white" },
        { icon: <Twitter className="w-5 h-5" />, href: "#", label: "Twitter", color: "hover:bg-teal-500 hover:!text-white" },
        { icon: <Instagram className="w-5 h-5" />, href: "#", label: "Instagram", color: "hover:bg-orange-500 hover:!text-white" },
        { icon: <Linkedin className="w-5 h-5" />, href: "#", label: "LinkedIn", color: "hover:bg-teal-500 hover:!text-white" },
        { icon: <Youtube className="w-5 h-5" />, href: "#", label: "YouTube", color: "hover:bg-orange-500 hover:!text-white" }
    ]

    return (
        <section className="bg-gradient-to-br from-gray-50 via-white to-gray-50 py-16">
            <div className="container mx-auto px-4 max-w-7xl">
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                                            <h2 className="text-lg font-bold text-gray-900 mb-4">Get in Touch</h2>
                    <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                        Have questions or need assistance? We're here to help! Reach out to us through any of the channels below.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Contact Information Cards */}
                    <div className="lg:col-span-1 space-y-6">
                        {contactInfo.map((info, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className={`bg-gradient-to-r ${info.bgColor} rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300`}
                            >
                                <div className={`bg-gradient-to-r ${info.color} rounded-xl p-3 w-fit mb-4 !text-white`}>
                                    {info.icon}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">{info.title}</h3>
                                <div className="space-y-2">
                                    {info.details.map((detail, detailIndex) => (
                                        <p key={detailIndex} className="text-gray-600 text-sm">{detail}</p>
                                    ))}
                                </div>
                            </motion.div>
                        ))}

                        {/* Social Media Links */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm"
                        >
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Follow Us</h3>
                            <div className="flex gap-3">
                                {socialLinks.map((social, index) => (
                                    <a
                                        key={index}
                                        href={social.href}
                                        className={`bg-gray-100 ${social.color} text-gray-600 rounded-lg p-3 transition-all duration-200 hover:scale-110`}
                                        aria-label={social.label}
                                    >
                                        {social.icon}
                                    </a>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
                        >
                            <div className="mb-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-2">Send us a Message</h3>
                                <p className="text-gray-600">
                                    Didn't find what you're looking for? Send us a detailed message and we'll respond as soon as possible.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Name and Email Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                name="name"
                                                value={name}
                                                onChange={handleInputChange}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                                                    errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                                placeholder="Enter your full name"
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {errors.name && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.name}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="email"
                                                name="email"
                                                value={email}
                                                onChange={handleInputChange}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                                                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                                placeholder="Enter your email address"
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {errors.email && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.email}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Phone and Subject Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={phone}
                                                onChange={handleInputChange}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                                                    errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                                placeholder="E.g.+256787000000 (optional)"
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {errors.phone && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.phone}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Subject <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                name="subject"
                                                value={subject}
                                                onChange={handleInputChange}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                                                    errors.subject ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                                placeholder="What is this about?"
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {errors.subject && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.subject}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                    </div>

                                {/* Message */}
                    <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Message <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="message"
                                        value={message}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 resize-none ${
                                            errors.message ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                        placeholder="Tell us more about your inquiry..."
                                    />
                                    <AnimatePresence>
                                        {errors.message && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                            >
                                                <AlertCircle className="w-4 h-4" />
                                                {errors.message}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Submit Button */}
                                <div className="flex items-center gap-4">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="flex items-center gap-2 bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 !text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5" />
                                                Send Message
                                            </>
                                        )}
                                    </button>

                                    <AnimatePresence>
                                        {formStatus === 'success' && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                className="flex items-center gap-2 text-green-600 text-sm"
                                            >
                                                <CheckCircle className="w-5 h-5" />
                                                Message sent successfully!
                                            </motion.div>
                                        )}
                                        {formStatus === 'error' && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                className="flex items-center gap-2 text-red-600 text-sm"
                                            >
                                                <AlertCircle className="w-5 h-5" />
                                                Failed to send message. Please try again.
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </div>

                {/* Map Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mt-12"
                >
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Find Us on the Map</h3>
                            <p className="text-sm text-gray-600">Visit our office at Makerere University</p>
                        </div>
                        <div className="h-96">
                            <iframe 
                                width="100%" 
                                height="100%" 
                                frameBorder="0" 
                                style={{ border: 0 }} 
                                src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBGY-aKP5KQbjJbf2B2mN6u9909uI5iv50&amp;q=makerere university" 
                                allowFullScreen="" 
                                title="EventPlots Location"
                                loading="lazy"
                            />
                        </div>
                    </div>
            </motion.div>
        </div>
        </section>
    )
}

export default Contact